/**
 * src/components/layout/BottomDock.tsx
 * Ultra-Premium Apple iOS 18 Floating Frosted Glass Tab Bar Dock
 * Enhanced with continuous squircles, tactile haptic feedback,
 * native audio clicks, and adaptive OLED Space Black & Crystal White modes.
 */

import React from 'react';
import { Layers, ArrowLeftRight, Plus, Clock, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { haptic, playTapSound } from '../../utils/audioHaptics';

interface BottomDockProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
  onNewEntryClick: () => void;
  pendingApprovalsCount?: number;
}

export const BottomDock: React.FC<BottomDockProps> = ({
  currentTab,
  onTabChange,
  onNewEntryClick,
  pendingApprovalsCount = 0
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const handleTabClick = (tab: string) => {
    haptic.tap();
    playTapSound();
    onTabChange(tab);
  };

  const handleFabClick = () => {
    haptic.button();
    playTapSound();
    onNewEntryClick();
  };

  return (
    <nav 
      className="ios-dock" 
      aria-label="iOS Mobile Navigation Dock"
      style={{
        boxShadow: '0 20px 48px -6px rgba(0, 0, 0, 0.75), 0 0 0 0.5px rgba(255, 255, 255, 0.12) inset',
      }}
    >
      {/* Tab 1: Home / Dashboard */}
      <button
        type="button"
        onClick={() => handleTabClick('dashboard')}
        className={`ios-dock-item ${currentTab === 'dashboard' ? 'active' : ''}`}
        title="Dashboard Wallet"
      >
        <Layers size={20} strokeWidth={currentTab === 'dashboard' ? 2.5 : 1.8} />
        <span>Home</span>
      </button>

      {/* Tab 2: Ledger */}
      <button
        type="button"
        onClick={() => handleTabClick('ledger')}
        className={`ios-dock-item ${currentTab === 'ledger' ? 'active' : ''}`}
        title="Transactions Ledger"
      >
        <ArrowLeftRight size={20} strokeWidth={currentTab === 'ledger' ? 2.5 : 1.8} />
        <span>Ledger</span>
      </button>

      {/* Center Floating Action Button (+) */}
      <button
        type="button"
        className="ios-dock-fab"
        onClick={handleFabClick}
        title={isAdmin ? 'Add Transaction' : 'Submit Entry Request'}
      >
        <Plus size={24} strokeWidth={2.8} />
      </button>

      {/* Tab 3: Approvals / Requests */}
      <button
        type="button"
        onClick={() => handleTabClick('approvals')}
        className={`ios-dock-item ${currentTab === 'approvals' ? 'active' : ''}`}
        style={{ position: 'relative' }}
        title={isAdmin ? 'Approvals Inbox' : 'My Requests'}
      >
        <Clock size={20} strokeWidth={currentTab === 'approvals' ? 2.5 : 1.8} />
        <span>{isAdmin ? 'Inbox' : 'Requests'}</span>
        {pendingApprovalsCount > 0 && (
          <span 
            style={{
              position: 'absolute',
              top: '5px',
              right: '12px',
              minWidth: '16px',
              height: '16px',
              padding: '0 4px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--ios-orange)',
              color: '#ffffff',
              fontSize: '0.625rem',
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 8px rgba(255, 159, 10, 0.8)'
            }}
          >
            {pendingApprovalsCount > 9 ? '9+' : pendingApprovalsCount}
          </span>
        )}
      </button>

      {/* Tab 4: Profile / Settings */}
      <button
        type="button"
        onClick={() => handleTabClick('profile')}
        className={`ios-dock-item ${currentTab === 'profile' ? 'active' : ''}`}
        title="CK ID Profile & Settings"
      >
        <User size={20} strokeWidth={currentTab === 'profile' ? 2.5 : 1.8} />
        <span>Profile</span>
      </button>

      <style>{`
        @media (min-width: 900px) {
          .ios-dock {
            display: none !important;
          }
        }
      `}</style>
    </nav>
  );
};
